// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EllipsisTypography } from '@/components/ellipsis-typography';
import { CheckRounded } from '@/constants/hardfin-standard-icons';
import { DecodedAssetTransitReportRow } from '@/report/api/get-asset-transit-report';
import { ReportTable } from '@/report/components/report-table';
import {
  createDayAggregationFunctions,
  createDayAggregationModel,
} from '@/report/utilities/create-day-aggregations';
import { getDates } from '@/report/utilities/get-dates';
import { isRowPresentOnDay } from '@/report/utilities/to-presence-bits';
import {
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridColDef,
  GridGroupingColDefOverride,
  GridPinnedColumnFields,
  isAutogeneratedRow,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { Interval } from 'luxon';
import { useMemo } from 'react';

type GridColumnDefinition = GridColDef<DecodedAssetTransitReportRow>;

const EXPORT_COLUMN_ORDER = ['productName', 'productSku', 'serial'];

const GROUPING_COLUMN_DEFINITION: GridGroupingColDefOverride = {
  leafField: 'serial',
  headerName: 'Product',
  disableExport: true,
  hideDescendantCount: true,
};

const PINNED_COLUMNS: GridPinnedColumnFields = {
  left: [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, 'serial'],
};

// The <DataGrid> always has a fixed  product, and asset column. Date columns
// are added dynamically based on the 'interval' prop provided to the component.
const COLUMN_DEFINITIONS: GridColumnDefinition[] = [
  {
    type: 'string',
    width: 200,
    field: 'productName',
    sortable: false,
    headerName: 'Product',
    // When grouped, display the product SKU alongside the name so the SKU is
    // visible in the grouping tree without needing a separate visible column.
    groupingValueGetter: (_, row) => {
      if (row.productSku) {
        return `${row.productSku} (${row.productName})`;
      }

      return row.productName;
    },
    renderCell: (parameters) => {
      return (
        <EllipsisTypography variant="body2" maximumWidth={168}>
          {parameters.formattedValue}
        </EllipsisTypography>
      );
    },
  },
  {
    type: 'string',
    width: 140,
    field: 'productSku',
    sortable: false,
    headerName: 'SKU',
    renderCell: (parameters) => {
      return (
        <EllipsisTypography variant="body2" maximumWidth={108}>
          {parameters.formattedValue}
        </EllipsisTypography>
      );
    },
  },
  {
    type: 'string',
    field: 'serial',
    sortable: false,
    headerName: 'Asset',
    renderCell: (parameters) => {
      return (
        <EllipsisTypography
          variant="body2"
          marginLeft="auto"
          maximumWidth={200}
        >
          {parameters.formattedValue}
        </EllipsisTypography>
      );
    },
  },
];

/**
 * Creates a column definition for a specific date that corresponds to a cell
 * index in a row.
 *
 * @param date - An ISO date string representing the columns's date.
 * @param dayIndex - The offset of the date from the start of the reported window.
 * @param dayCount - How many days the server reported, which bounds the bit mask.
 */
function createDateColumnDefinition(
  date: string,
  dayIndex: number,
  dayCount: number
) {
  const columnDefinition: GridColumnDefinition = {
    type: 'number',
    align: 'right',
    width: 100,
    field: date,
    sortable: false,
    headerName: date,
    valueGetter: (value, row) => {
      if (isAutogeneratedRow(row)) {
        return value;
      }

      return isRowPresentOnDay(row, dayIndex, dayCount) ? 1 : 0;
    },
    renderCell: (parameters) => {
      if (isAutogeneratedRow(parameters.row)) {
        return parameters.formattedValue;
      }

      if (isRowPresentOnDay(parameters.row, dayIndex, dayCount)) {
        return <CheckRounded color="success" fontSize="small" />;
      }

      return null;
    },
  };

  return columnDefinition;
}

/**
 * Creates an aggregation model for the <DataGrid> component that maps each
 * date in an interval to the 'assetCount' aggregation function.
 *
 * @param interval - An Interval representing the date range.
 * @param dayCount - How many days the server reported.
 */
function createAggregationModel(interval: Interval, dayCount: number) {
  return createDayAggregationModel(getDates(interval), dayCount);
}

interface Props {
  /**
   * A collection of rows to display in the table.
   */
  data: DecodedAssetTransitReportRow[];

  /**
   * The interval of the report.
   */
  interval: Interval;

  /**
   * How many days the server reported. Bit masks are read against this, not the interval.
   */
  dayCount: number;

  /**
   * If true, the table is displayed in a loading state.
   */
  isLoading?: boolean;

  /**
   * The maximum height of the table. This is used to make the table scrollable.
   */
  maximumHeight: number;

  /**
   * A callback fired when the user clicks the export button that returns the
   * name of the file to be exported. This file name should not include a file
   * extension.
   */
  getFileName: () => string;
}

/**
 * A data table that displays rows of asset transit data over a date range.
 */
export function AssetTransitReportTable(props: Props) {
  const apiRef = useGridApiRef();

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      pinnedColumns: PINNED_COLUMNS,
      // The serial column is hidden because it is used as the leaf node for
      // the grouping column.
      columns: {
        columnVisibilityModel: { serial: false, productSku: false },
      },
      rowGrouping: { model: ['productName'] },
    },
  });

  const aggregationModel = useMemo(
    () => createAggregationModel(props.interval, props.dayCount),
    [props.interval, props.dayCount]
  );

  const aggregationFunctions = useMemo(
    () => createDayAggregationFunctions(props.dayCount),
    [props.dayCount]
  );

  const columnDefinitions = useMemo(() => {
    const dates = getDates(props.interval);

    return dates.reduce((columnDefinitions, date, index) => {
      return [
        ...columnDefinitions,
        createDateColumnDefinition(date, index, props.dayCount),
      ];
    }, COLUMN_DEFINITIONS);
  }, [props.interval, props.dayCount]);

  return (
    <ReportTable
      rows={props.data}
      apiRef={apiRef}
      loading={props.isLoading}
      columns={columnDefinitions}
      initialState={initialState}
      getFileName={props.getFileName}
      maximumHeight={props.maximumHeight}
      aggregationModel={aggregationModel}
      groupingColDef={GROUPING_COLUMN_DEFINITION}
      exportColumnOrder={EXPORT_COLUMN_ORDER}
      aggregationFunctions={aggregationFunctions}
    />
  );
}
